import React, { ReactNode, useEffect, useMemo } from 'react';
import { Layout, Menu, theme, Button, App as AntdApp, Badge, Popconfirm, Input } from 'antd';
import type { MenuProps } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  SafetyOutlined,
  ClearOutlined,
  BellOutlined,
  PhoneOutlined,
  CompassOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  ShareAltOutlined,
  StopOutlined,
  FolderOpenOutlined,
  CustomerServiceOutlined,
  LaptopOutlined,
  ReadOutlined,
  FileProtectOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import granthaIcon from '../icons/Grantha.png';
import contributorsIcon from '../icons/contributors.png';
import languageIcon from '../icons/language.png';
import countryIcon from '../icons/country.png';
import hierarchyIcon from '../icons/hierarchy.png';
import vyakhyanuvadIcon from '../icons/vyakhyanuvad.png';
import savedProfileIcon from '../icons/savedprofile.png';
import purohitIcon from '../icons/purohit.png';
import vyakhyanuvadViewerIcon from '../icons/vyakhyanuvadviewer.png';
import granthaViewIcon from '../icons/granthaview.png';
import anukramanikaIcon from '../icons/anukramanika.png';
import bidhiIcon from '../icons/vidhi.png';
import karmakandaIcon from '../icons/karmakanda.png';
import sankalpaIcon from '../icons/sankalpa.png';
import pujaIcon from '../icons/puja.png';
import samagriIcon from '../icons/samagri.png';
import gotraIcon from '../icons/gotra.png';
import vedsakhaIcon from '../icons/vedsakha.png';
import relationIcon from '../icons/relation.png';
import { getCookie, setCookie } from '../utils/cookies';
import { API_URL } from '../config/apiUrls';
import { getAccessToken } from '../utils/authTokens';
import { isCurrentUserLibAdmin, isAdminOnlyEnabled } from '../utils/authPolicy';
import { logoutSession } from '../services/authSession';
import { disconnectPresence, ensurePresenceConnected, usePresence } from '../hooks/usePresence';
import {
  ensureIncomingCallListeners,
  resetIncomingCallRing,
  useIncomingCallRing,
} from '../hooks/useIncomingCallRing';
import IncomingCallOverlay from '../components/call/IncomingCallOverlay';
import { useChatStore } from '../hooks/useChatStore';
import './MainLayout.css';

const { Header, Content, Sider } = Layout;

interface MainLayoutProps {
  children: ReactNode;
}

const MenuIcon: React.FC<{ src: string; alt: string; collapsed?: boolean }> = ({
  src,
  alt,
  collapsed = false,
}) => (
  <img
    src={src}
    alt={alt}
    style={{
      width: collapsed ? 16 : 14,
      height: collapsed ? 16 : 14,
      objectFit: 'contain',
      display: 'inline-block',
      verticalAlign: 'middle',
    }}
  />
);

/** Extract plain searchable text from Ant Design menu labels (string or React nodes). */
function getMenuLabelText(label: unknown): string {
  if (label == null || typeof label === 'boolean') return '';
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  if (Array.isArray(label)) {
    return label.map(getMenuLabelText).filter(Boolean).join(' ');
  }
  if (React.isValidElement(label)) {
    const props = label.props as { children?: unknown; alt?: string };
    const fromChildren = getMenuLabelText(props.children);
    if (fromChildren) return fromChildren;
    if (typeof props.alt === 'string') return props.alt;
  }
  return '';
}

function filterMenuItems(
  items: MenuProps['items'],
  query: string,
): MenuProps['items'] {
  const q = query.trim().toLowerCase();
  if (!q || !items) return items;

  const filtered: any[] = [];

  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const anyItem = item as any;
    if (anyItem.type === 'divider') continue;

    const labelText = getMenuLabelText(anyItem.label).toLowerCase();
    const keyText = String(anyItem.key ?? '').toLowerCase();
    const selfMatch = labelText.includes(q) || keyText.includes(q);

    if (Array.isArray(anyItem.children)) {
      if (selfMatch) {
        // Group title matched — keep all children
        filtered.push(item);
        continue;
      }
      const matchedChildren = anyItem.children.filter((child: any) => {
        if (!child || typeof child !== 'object') return false;
        const childLabel = getMenuLabelText(child.label).toLowerCase();
        const childKey = String(child.key ?? '').toLowerCase();
        return childLabel.includes(q) || childKey.includes(q);
      });
      if (matchedChildren.length > 0) {
        filtered.push({ ...anyItem, children: matchedChildren });
      }
      continue;
    }

    if (selfMatch) {
      filtered.push(item);
    }
  }

  return filtered;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { message } = AntdApp.useApp();
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = getCookie('sidebar-collapsed');
    return saved === 'true';
  });
  const [isAuthenticated, setIsAuthenticated] = React.useState(
    () => Boolean(getAccessToken()),
  );
  const [username, setUsername] = React.useState<string>('');
  const [clearingCache, setClearingCache] = React.useState(false);
  const [menuSearch, setMenuSearch] = React.useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    resetIncomingCallRing();
    disconnectPresence();
    await logoutSession();
    navigate('/login');
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_URL}/cache/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = `Failed to clear cache (${response.status})`;
        try {
          const data = await response.json();
          if (Array.isArray(data?.message)) {
            errorMessage = data.message.join(', ');
          } else if (typeof data?.message === 'string') {
            errorMessage = data.message;
          }
        } catch {
          // ignore JSON parse errors
        }
        message.error(errorMessage);
        return;
      }

      message.success('Cache cleared successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to clear cache';
      message.error(errorMessage);
    } finally {
      setClearingCache(false);
    }
  };

  useEffect(() => {
    setCookie('sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  useEffect(() => {
    const token = getAccessToken();
    const isAuthed = Boolean(token);
    setIsAuthenticated(isAuthed);
    if (!token) {
      setUsername('');
      resetIncomingCallRing();
      disconnectPresence();
      return;
    }
    if (isAdminOnlyEnabled() && !isCurrentUserLibAdmin()) {
      void handleLogout();
      return;
    }
    ensurePresenceConnected(token);
    ensureIncomingCallListeners();
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUsername(payload.username || 'User');
    } catch {
      setUsername('User');
    }
  }, [location.pathname]);

  // Keep /signaling connected while authenticated so accepted peers see us as active.
  usePresence();
  useIncomingCallRing();
  const { totalUnreadCount, refreshThreads } = useChatStore();

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshThreads().catch(() => undefined);
  }, [isAuthenticated, refreshThreads]);
  
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    const mouseEvent = domEvent as unknown as React.MouseEvent;
    if (mouseEvent?.metaKey || mouseEvent?.ctrlKey || mouseEvent?.button === 1) {
      return;
    }
    navigate(String(key));
  };

  const baseMenuItems = useMemo<MenuProps['items']>(
    () => [
      // ----------------------------------------------------
      // 1. ग्रन्थ
      // ----------------------------------------------------
      {
        key: 'group-grantha',
        icon: <MenuIcon src={granthaIcon} alt="ग्रन्थ" collapsed={collapsed} />,
        label: 'ग्रन्थ',
        children: [
          {
            key: '/',
            icon: <MenuIcon src={granthaIcon} alt="Grantha" collapsed={collapsed} />,
            label: 'Grantha',
          },
          {
            key: '/vyakhyanuvad',
            icon: <MenuIcon src={vyakhyanuvadIcon} alt="Vyakhyanuvad" collapsed={collapsed} />,
            label: 'Vyakhyanuvad',
          },
          {
            key: '/vyakhyanuvad-viewer',
            icon: (
              <MenuIcon src={vyakhyanuvadViewerIcon} alt="Vyakhyanuvad Viewer" collapsed={collapsed} />
            ),
            label: 'Vyakhyanuvad Viewer',
          },
          {
            key: '/hierarchy',
            icon: <MenuIcon src={hierarchyIcon} alt="Hierarchy" collapsed={collapsed} />,
            label: 'Hierarchy',
          },
          {
            key: '/grantha-viewer-new',
            icon: <MenuIcon src={granthaViewIcon} alt="Grantha Viewer New" collapsed={collapsed} />,
            label: 'Grantha Viewer New',
          },
          {
            key: '/anukramanika',
            icon: <MenuIcon src={anukramanikaIcon} alt="Anukramanika" collapsed={collapsed} />,
            label: 'Anukramanika',
          },
        ],
      },

      // ----------------------------------------------------
      // 2. कर्मकाण्ड
      // ----------------------------------------------------
      {
        key: 'group-karmakanda',
        icon: <MenuIcon src={karmakandaIcon} alt="कर्मकाण्ड" collapsed={collapsed} />,
        label: 'कर्मकाण्ड',
        children: [
          {
            key: '/bidhi',
            icon: <MenuIcon src={bidhiIcon} alt="Bidhi" collapsed={collapsed} />,
            label: 'Bidhi',
          },
          {
            key: '/bidhi-viewer-new',
            icon: <MenuIcon src={bidhiIcon} alt="Bidhi Viewer New" collapsed={collapsed} />,
            label: 'Bidhi Viewer New',
          },
          {
            key: '/popular-books-bidhi',
            icon: <CheckCircleOutlined />,
            label: 'Popular Books / Bidhi',
          },
          {
            key: '/karmakanda',
            icon: <MenuIcon src={karmakandaIcon} alt="Karmakanda" collapsed={collapsed} />,
            label: 'Karmakanda',
          },
          {
            key: '/sankalpa',
            icon: <MenuIcon src={sankalpaIcon} alt="Sankalpa" collapsed={collapsed} />,
            label: 'Sankalpa',
          },
          {
            key: '/byk',
            icon: <MenuIcon src={bidhiIcon} alt="BYK" collapsed={collapsed} />,
            label: 'BYK',
          },
          {
            key: '/puja',
            icon: <MenuIcon src={pujaIcon} alt="Puja" collapsed={collapsed} />,
            label: 'Puja',
          },
          {
            key: '/samagri',
            icon: <MenuIcon src={samagriIcon} alt="Samagri" collapsed={collapsed} />,
            label: 'Samagri',
          },
          {
            key: '/mantra-japa',
            icon: <CustomerServiceOutlined />,
            label: 'Mantra Japa',
          },
          {
            key: '/daily-shloka',
            icon: <ReadOutlined />,
            label: 'Daily Shloka',
          },
        ],
      },

      // ----------------------------------------------------
      // 3. पञ्चाङ्ग
      // ----------------------------------------------------
      {
        key: 'group-panchanga',
        icon: <CalendarOutlined />,
        label: 'पञ्चाङ्ग',
        children: [
          {
            key: '/festivals',
            icon: <CalendarOutlined />,
            label: 'Festivals',
          },
          {
            key: '/patro',
            icon: <CalendarOutlined />,
            label: 'Patro',
          },
          {
            key: '/approved-to-display',
            icon: <CheckCircleOutlined />,
            label: 'Approved To Display',
          },
        ],
      },

      // ----------------------------------------------------
      // 4. प्रयोगकर्ता
      // ----------------------------------------------------
      {
        key: 'group-users',
        icon: <TeamOutlined />,
        label: 'प्रयोगकर्ता',
        children: [
          {
            key: '/users',
            icon: <TeamOutlined />,
            label: 'User Management',
          },
          {
            key: '/active',
            icon: <UserOutlined style={{ color: '#52c41a' }} />,
            label: 'Admin Dashboard',
          },
          {
            key: '/suspension',
            icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
            label: 'Suspensions',
          },
          {
            key: '/profile',
            icon: <UserOutlined />,
            label: 'My Profile',
          },
          {
            key: '/profile/saved',
            icon: <MenuIcon src={savedProfileIcon} alt="Saved Profiles" collapsed={collapsed} />,
            label: 'Saved Profiles',
          },
          {
            key: '/profile/shared',
            icon: <ShareAltOutlined />,
            label: 'Shared profiles',
          },
          {
            key: '/devices',
            icon: <LaptopOutlined />,
            label: 'Logged-in Devices',
          },
          {
            key: '/purohits',
            icon: <MenuIcon src={purohitIcon} alt="Purohits" collapsed={collapsed} />,
            label: 'Purohits',
          },
          {
            key: '/jyotish',
            icon: <CompassOutlined />,
            label: 'Jyotish',
          },
        ],
      },

      // ----------------------------------------------------
      // 5. सञ्चार
      // ----------------------------------------------------
      {
        key: 'group-communication',
        icon: <MessageOutlined />,
        label:
          totalUnreadCount > 0 ? (
            <Badge count={totalUnreadCount} size="small" offset={[10, 0]}>
              <span>सञ्चार</span>
            </Badge>
          ) : (
            'सञ्चार'
          ),
        children: [
          {
            key: '/call',
            icon: <PhoneOutlined />,
            label: 'Call',
          },
          {
            key: '/connections',
            icon: <TeamOutlined />,
            label: 'Connections',
          },
          {
            key: '/chats',
            icon: <MessageOutlined />,
            label:
              totalUnreadCount > 0 ? (
                <Badge count={totalUnreadCount} size="small" offset={[10, 0]}>
                  <span>Chats</span>
                </Badge>
              ) : (
                'Chats'
              ),
          },
          {
            key: '/media',
            icon: <FolderOpenOutlined />,
            label: 'Media',
          },
        ],
      },

      // ----------------------------------------------------
      // 6. वंश
      // ----------------------------------------------------
      {
        key: 'group-lineage',
        icon: <MenuIcon src={relationIcon} alt="वंश" collapsed={collapsed} />,
        label: 'वंश',
        children: [
          {
            key: '/gotra',
            icon: <MenuIcon src={gotraIcon} alt="Gotra" collapsed={collapsed} />,
            label: 'Gotra',
          },
          {
            key: '/vedsakhas',
            icon: <MenuIcon src={vedsakhaIcon} alt="Vedsakha" collapsed={collapsed} />,
            label: 'Vedsakha',
          },
          {
            key: '/relations',
            icon: <MenuIcon src={relationIcon} alt="Relations" collapsed={collapsed} />,
            label: 'Relations',
          },
        ],
      },

      // ----------------------------------------------------
      // 7. प्रणाली
      // ----------------------------------------------------
      {
        key: 'group-system',
        icon: <SettingOutlined />,
        label: 'प्रणाली',
        children: [
          {
            key: '/contributors',
            icon: <MenuIcon src={contributorsIcon} alt="Contributors" collapsed={collapsed} />,
            label: 'Contributors',
          },
          {
            key: '/languages',
            icon: <MenuIcon src={languageIcon} alt="Language" collapsed={collapsed} />,
            label: 'Language',
          },
          {
            key: '/countries',
            icon: <MenuIcon src={countryIcon} alt="Country" collapsed={collapsed} />,
            label: 'Country',
          },
          {
            key: '/policies',
            icon: <SafetyOutlined />,
            label: 'Policies',
          },
          {
            key: '/privacy-policy-admin',
            icon: <FileProtectOutlined />,
            label: 'Privacy Policy',
          },
          {
            key: '/gis',
            icon: <EnvironmentOutlined />,
            label: 'GIS Management',
          },
          {
            key: '/notifications',
            icon: <BellOutlined />,
            label: 'Notifications',
          },
        ],
      },
    ],
    [collapsed, totalUnreadCount],
  );

  const ALL_GROUP_KEYS = useMemo(
    () => [
      'group-grantha',
      'group-karmakanda',
      'group-panchanga',
      'group-users',
      'group-communication',
      'group-lineage',
      'group-system',
    ],
    [],
  );

  const getGroupForPath = (path: string): string | undefined => {
    for (const group of baseMenuItems ?? []) {
      if (group && 'children' in group && Array.isArray(group.children)) {
        for (const child of group.children) {
          if (child && 'key' in child && typeof child.key === 'string') {
            if (path === child.key || (child.key !== '/' && path.startsWith(child.key))) {
              return String(group.key);
            }
          }
        }
      }
    }
    return undefined;
  };

  const [openKeys, setOpenKeys] = React.useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('sidebar-open-keys');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      // ignore
    }
    return ALL_GROUP_KEYS;
  });

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
    try {
      localStorage.setItem('sidebar-open-keys', JSON.stringify(keys));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const currentGroup = getGroupForPath(location.pathname);
    if (currentGroup) {
      setOpenKeys((prev) => {
        if (prev.includes(currentGroup)) return prev;
        const updated = [...prev, currentGroup];
        try {
          localStorage.setItem('sidebar-open-keys', JSON.stringify(updated));
        } catch {
          // ignore
        }
        return updated;
      });
    }
  }, [location.pathname]);

  const filteredBaseMenuItems = useMemo(
    () => filterMenuItems(baseMenuItems, menuSearch),
    [baseMenuItems, menuSearch],
  );

  const menuItems = useMemo<MenuProps['items']>(() => {
    const processItem = (item: any): any => {
      if (!item || typeof item !== 'object') return item;

      // Handle SubMenu with children
      if (Array.isArray(item.children)) {
        return {
          ...item,
          children: item.children.map(processItem),
        };
      }

      if (!('label' in item)) return item;
      const label = item.label;
      const itemKey = typeof item.key === 'string' ? item.key : '';
      const isRoute = itemKey.startsWith('/');
      const wrappedLabel = isRoute ? (
        <Link
          to={itemKey}
          style={{ color: 'inherit', display: 'inline-block', width: '100%' }}
        >
          {label}
        </Link>
      ) : (
        label
      );

      return {
        ...item,
        label: wrappedLabel,
      };
    };

    return (filteredBaseMenuItems ?? []).map(processItem);
  }, [filteredBaseMenuItems]);

  // While searching, expand all groups that still have matches
  const effectiveOpenKeys = useMemo(() => {
    if (!menuSearch.trim() || collapsed) return openKeys;
    return (filteredBaseMenuItems ?? [])
      .map((item) => (item && 'key' in item ? String(item.key) : ''))
      .filter((key) => key.startsWith('group-'));
  }, [menuSearch, collapsed, openKeys, filteredBaseMenuItems]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
        }}
      >
        <h1 style={{ color: 'white', margin: 0 }}>Grantha Management System</h1>
        {isAuthenticated ? (
          <Link to="/profile" style={{ color: 'white' }}>
            {username}
          </Link>
        ) : (
          <Link to="/login" style={{ color: 'white' }}>
            Log in
          </Link>
        )}
      </Header>
      <Layout>
        {isAuthenticated && (
        <Sider
          className="main-layout-sider"
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => {
            setCollapsed(value);
            if (value) setMenuSearch('');
          }}
          width={200}
          collapsedWidth={64}
          style={{
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            overflow: 'hidden',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              paddingBottom: 48,
            }}
          >
            {!collapsed && (
              <div className="main-layout-menu-search">
                <Input
                  allowClear
                  size="small"
                  prefix={<SearchOutlined style={{ color: 'rgba(255,255,255,0.45)' }} />}
                  placeholder="मेनु खोज्नुहोस्… (Search menu)"
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                  className="main-layout-menu-search-input"
                />
              </div>
            )}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Menu
                className="main-layout-menu"
                theme="dark"
                mode="inline"
                inlineCollapsed={collapsed}
                onClick={handleMenuClick}
                openKeys={collapsed ? undefined : effectiveOpenKeys}
                onOpenChange={(keys) => {
                  if (menuSearch.trim()) return;
                  handleOpenChange(keys);
                }}
                forceSubMenuRender={true}
                selectedKeys={[
                  location.pathname.startsWith('/active')
                    ? '/active'
                    : location.pathname.startsWith('/profile/shared')
                    ? '/profile/shared'
                    : location.pathname.startsWith('/profile/saved')
                    ? '/profile/saved'
                    : location.pathname.startsWith('/purohits')
                      ? '/purohits'
                      : location.pathname.startsWith('/jyotish')
                        ? '/jyotish'
                        : location.pathname.startsWith('/chats')
                          ? '/chats'
                        : location.pathname,
                ]}
                style={{ borderInlineEnd: 'none' }}
                items={menuItems}
              />
              {!collapsed &&
                menuSearch.trim() &&
                (filteredBaseMenuItems?.length ?? 0) === 0 && (
                  <div className="main-layout-menu-search-empty">
                    कुनै मेनु भेटिएन
                  </div>
                )}
            </div>
            <div
              style={{
                flexShrink: 0,
                padding: collapsed ? '8px 0' : '8px 16px',
                borderTop: '1px solid #434343',
                backgroundColor: '#001529',
              }}
            >
              <Popconfirm
                title="Clear cache?"
                description="This will reset the server cache. Continue?"
                onConfirm={handleClearCache}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  loading={clearingCache}
                  style={{ color: 'white', width: '100%', marginBottom: '8px' }}
                  block
                >
                  {!collapsed && 'Clear Cache'}
                </Button>
              </Popconfirm>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{ color: 'white', width: '100%' }}
                block
              >
                {!collapsed && 'Logout'}
              </Button>
            </div>
          </div>
        </Sider>
        )}
        <Layout style={{ minWidth: 0, maxWidth: '100%' }}>
          <Content
            style={{
              margin: 0,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              minHeight: 280,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
      {isAuthenticated ? <IncomingCallOverlay /> : null}
    </Layout>
  );
};

export default MainLayout;